import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

const STORIES_STORAGE_KEY = 'petso_stories';
const STORY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface StoryItem {
  id: string;
  /** Blob URL or base64 data URL (for persistence) */
  url: string;
  isVideo: boolean;
  createdAt: number;
}

interface StoriesContextValue {
  /** User's stories (newest last for display order) */
  ownStories: StoryItem[];
  addStory: (url: string, isVideo: boolean) => void;
  /** Add from File - images are persisted as base64, videos use blob (lost on refresh) */
  addStoryFromFile: (file: File) => Promise<void>;
  removeStory: (id: string) => void;
  removeAllStories: () => void;
  /** Filter stories that are still within 24h */
  getActiveStories: () => StoryItem[];
}

const StoriesContext = createContext<StoriesContextValue | null>(null);

function loadStoriesFromStorage(): StoryItem[] {
  try {
    const raw = localStorage.getItem(STORIES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoryItem[];
    const now = Date.now();
    return parsed.filter((s) => now - s.createdAt < STORY_TTL_MS);
  } catch {
    return [];
  }
}

function saveStoriesToStorage(stories: StoryItem[]) {
  try {
    const now = Date.now();
    const valid = stories.filter((s) => now - s.createdAt < STORY_TTL_MS);
    localStorage.setItem(STORIES_STORAGE_KEY, JSON.stringify(valid));
  } catch {
    // ignore
  }
}

export function StoriesProvider({ children }: { children: ReactNode }) {
  const [ownStories, setOwnStories] = useState<StoryItem[]>(() =>
    loadStoriesFromStorage()
  );

  useEffect(() => {
    saveStoriesToStorage(ownStories);
  }, [ownStories]);

  const addStory = useCallback((url: string, isVideo: boolean) => {
    const id = `story-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setOwnStories((prev) => {
      const filtered = prev.filter(
        (s) => Date.now() - s.createdAt < STORY_TTL_MS
      );
      return [...filtered, { id, url, isVideo, createdAt: Date.now() }];
    });
  }, []);

  const addStoryFromFile = useCallback(
    async (file: File) => {
      const isVideo = file.type.startsWith('video/');
      if (isVideo) {
        const url = URL.createObjectURL(file);
        addStory(url, true);
        return;
      }
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          addStory(dataUrl, false);
          resolve();
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    },
    [addStory]
  );

  const removeStory = useCallback((id: string) => {
    setOwnStories((prev) => {
      const item = prev.find((s) => s.id === id);
      if (item?.url.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
      return prev.filter((s) => s.id !== id);
    });
  }, []);

  const removeAllStories = useCallback(() => {
    setOwnStories((prev) => {
      prev.forEach((s) => {
        if (s.url.startsWith('blob:')) URL.revokeObjectURL(s.url);
      });
      return [];
    });
  }, []);

  const getActiveStories = useCallback(() => {
    const now = Date.now();
    return ownStories.filter((s) => now - s.createdAt < STORY_TTL_MS);
  }, [ownStories]);

  const value: StoriesContextValue = {
    ownStories,
    addStory,
    addStoryFromFile,
    removeStory,
    removeAllStories,
    getActiveStories,
  };

  return (
    <StoriesContext.Provider value={value}>{children}</StoriesContext.Provider>
  );
}

export function useStories(): StoriesContextValue {
  const ctx = useContext(StoriesContext);
  if (!ctx) {
    throw new Error('useStories must be used within StoriesProvider');
  }
  return ctx;
}
